/**
 * Undo/redo for the floor-map editor (§5.2).
 *
 * The scene is one immutable document, and history is an explicit stack of
 * whole snapshots. The important distinction is `preview` vs `commit`:
 *
 *   • `preview` swaps the displayed document without touching history, so a
 *     drag that fires sixty pointermove events still costs zero undo steps.
 *   • `commit` pushes the *last committed* document onto the past and makes the
 *     new one current — one entry per finished gesture.
 *
 * `cancel` (Escape) throws away whatever the in-flight gesture previewed by
 * restoring the last committed document, which is why the two are kept apart.
 */
import { useCallback, useMemo, useReducer } from 'react';
import type { Scene } from './scene';

const HISTORY_LIMIT = 100;

interface HistoryState {
  past: Scene[];
  /** What is on screen — may be a mid-gesture preview. */
  present: Scene;
  /** The last document a completed gesture produced. */
  committed: Scene;
  future: Scene[];
  /** The document the server currently holds. */
  saved: Scene;
}

/** A step may be a document or a function of the current one. */
type Next = Scene | ((current: Scene) => Scene);

type Action =
  | { type: 'preview'; next: Next }
  | { type: 'commit'; next: Next }
  | { type: 'cancel' }
  | { type: 'undo' }
  | { type: 'redo' }
  | { type: 'reset'; scene: Scene }
  | { type: 'markSaved' };

function resolve(next: Next, current: Scene): Scene {
  return typeof next === 'function' ? next(current) : next;
}

function reducer(state: HistoryState, action: Action): HistoryState {
  switch (action.type) {
    case 'preview': {
      const scene = resolve(action.next, state.present);
      return scene === state.present ? state : { ...state, present: scene };
    }

    case 'commit': {
      const scene = resolve(action.next, state.present);
      if (scene === state.committed) return { ...state, present: state.committed };
      const past = [...state.past, state.committed];
      return {
        ...state,
        past: past.length > HISTORY_LIMIT ? past.slice(past.length - HISTORY_LIMIT) : past,
        present: scene,
        committed: scene,
        future: [],
      };
    }

    case 'cancel':
      return state.present === state.committed ? state : { ...state, present: state.committed };

    case 'undo': {
      if (state.past.length === 0) return state;
      const previous = state.past[state.past.length - 1];
      return {
        ...state,
        past: state.past.slice(0, -1),
        present: previous,
        committed: previous,
        future: [state.committed, ...state.future],
      };
    }

    case 'redo': {
      if (state.future.length === 0) return state;
      const [next, ...rest] = state.future;
      return {
        ...state,
        past: [...state.past, state.committed],
        present: next,
        committed: next,
        future: rest,
      };
    }

    case 'reset':
      return {
        past: [],
        present: action.scene,
        committed: action.scene,
        future: [],
        saved: action.scene,
      };

    case 'markSaved':
      return { ...state, saved: state.committed };

    default:
      return state;
  }
}

export interface SceneHistory {
  scene: Scene;
  /** Show a document without recording it — use during a gesture. */
  preview: (next: Next) => void;
  /** Record one undoable step. */
  commit: (next: Next) => void;
  /** Escape: drop the in-flight preview. */
  cancel: () => void;
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  /** Unsaved changes exist — compared by reference, snapshots are immutable. */
  dirty: boolean;
  /** Load a document from the server; clears history. */
  reset: (scene: Scene) => void;
  markSaved: () => void;
  depth: { past: number; future: number };
}

export function useSceneHistory(initial: Scene): SceneHistory {
  const [state, dispatch] = useReducer(reducer, initial, (scene) => ({
    past: [],
    present: scene,
    committed: scene,
    future: [],
    saved: scene,
  }));

  // Every callback is dispatch-only, so its identity is stable for the life of
  // the editor: an effect may depend on `reset` without re-running on each edit.
  const preview = useCallback((next: Next) => dispatch({ type: 'preview', next }), []);
  const commit = useCallback((next: Next) => dispatch({ type: 'commit', next }), []);
  const cancel = useCallback(() => dispatch({ type: 'cancel' }), []);
  const undo = useCallback(() => dispatch({ type: 'undo' }), []);
  const redo = useCallback(() => dispatch({ type: 'redo' }), []);
  const reset = useCallback((scene: Scene) => dispatch({ type: 'reset', scene }), []);
  const markSaved = useCallback(() => dispatch({ type: 'markSaved' }), []);

  return useMemo<SceneHistory>(
    () => ({
      scene: state.present,
      preview,
      commit,
      cancel,
      undo,
      redo,
      canUndo: state.past.length > 0,
      canRedo: state.future.length > 0,
      dirty: state.committed !== state.saved,
      reset,
      markSaved,
      depth: { past: state.past.length, future: state.future.length },
    }),
    [state, preview, commit, cancel, undo, redo, reset, markSaved],
  );
}
